import { useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Search, Plus } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const KINDS = [
  { k: "interno", l: "Dipendenti" },
  { k: "esterno", l: "Esterni" },
  { k: "mezzo", l: "Mezzi" },
  { k: "mezzo_cantiere", l: "Mezzi cantiere" },
  { k: "attrezzatura", l: "Attrezzature" },
];

// Iter 119: "+ Aggiungi" nella colonna: scegli dalle risorse con ricerca e spunte (niente trascinamento)
export default function AggiungiDialog({ open, onClose, sede, data, catalog, onDone }) {
  const [kind, setKind] = useState("interno");
  const [q, setQ] = useState("");
  const [sel, setSel] = useState([]);
  const [busy, setBusy] = useState(false);
  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    return catalog.filter((i) => i.tipo === kind && i.sedeId !== sede.id && (!s || i.label.toLowerCase().includes(s))).sort((a, b) => (a.dove ? 1 : 0) - (b.dove ? 1 : 0) || a.label.localeCompare(b.label));
  }, [catalog, kind, q, sede.id]);
  const key = (i) => `${i.tipo}-${i.id}`;
  const toggle = (i) => setSel((x) => (x.includes(key(i)) ? x.filter((k) => k !== key(i)) : [...x, key(i)]));

  const save = async () => {
    const items = catalog.filter((i) => sel.includes(key(i))).map((i) => ({ risorsa_tipo: i.tipo, risorsa_id: i.id, sede_id: sede.id }));
    if (!items.length) return;
    setBusy(true);
    try {
      const { data: r } = await api.put(`/programma/${data}/move-many`, { items, sede_id: sede.id });
      toast.success(`${r.spostati} aggiunti a ${sede.nome}${r.saltati_assenti?.length ? ` · saltati (in assenza): ${r.saltati_assenti.join(", ")}` : ""}`);
      setSel([]); setQ("");
      onDone?.(); onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Operazione non riuscita");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] flex flex-col" data-testid="aggiungi-dialog">
        <DialogHeader><DialogTitle className="font-heading">Aggiungi a {sede.nome}</DialogTitle></DialogHeader>
        <div className="flex flex-wrap gap-1">
          {KINDS.map((t) => (
            <button key={t.k} type="button" onClick={() => setKind(t.k)} data-testid={`aggiungi-kind-${t.k}`}
              className={`rounded-full px-2.5 py-1 text-xs font-bold border ${kind === t.k ? "bg-[#0044FF] text-white border-[#0044FF]" : "bg-white text-slate-700 border-slate-300"}`}>
              {t.l} <span className="opacity-70">{catalog.filter((i) => i.tipo === t.k && i.sedeId !== sede.id).length}</span>
            </button>
          ))}
        </div>
        <div className="relative"><Search className="h-3.5 w-3.5 absolute left-2 top-2.5 text-slate-400" /><Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca…" className="h-8 pl-7 text-sm" data-testid="aggiungi-search" /></div>
        <div className="flex-1 overflow-y-auto border rounded-md divide-y min-h-[200px]" data-testid="aggiungi-list">
          {list.length === 0 && <div className="p-4 text-sm text-slate-500">Nessuna risorsa disponibile</div>}
          {list.map((i) => (
            <label key={key(i)} className={`flex items-center gap-2 px-2 py-1.5 text-sm cursor-pointer hover:bg-slate-50 ${i.assenza ? "opacity-50" : ""}`} data-testid={`aggiungi-item-${i.id}`}>
              <input type="checkbox" className="h-4 w-4" checked={sel.includes(key(i))} onChange={() => toggle(i)} disabled={!!i.assenza} />
              <span className="font-semibold flex-1 truncate">{i.label}</span>
              <span className="text-[11px] text-slate-500 shrink-0">{i.assenza ? `in ${i.assenza}` : i.dove ? `ora: ${i.dove}` : "libero"}</span>
            </label>
          ))}
        </div>
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs text-slate-500">{sel.length} selezionati · chi era in un altro cantiere viene spostato</span>
          <Button onClick={save} disabled={busy || !sel.length} className="bg-[#0044FF] hover:bg-[#0033CC]" data-testid="aggiungi-confirm">
            {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />} Aggiungi
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
